import { useEffect, useMemo } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetEnterpriseOrdersNewQuery } from "store/api/enterpriseApi"
import { UITypes } from "types"
import styles from "assets/scss/index.module.scss"

import { convertEnterpriseOrdersNewToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import TableWrapper from "components/table/TableWrapper"

import { enterpriseOrdersTableHeaderCells } from "./enterpriseOrdersTableHeaderCells"

const TABLE_NAME = "admin_orders_list"

export const EnterpriseOrdersPage = () => {
  const [triggerGetOrders, { isFetching, data }] = useLazyGetEnterpriseOrdersNewQuery()
  const [triggerGetTableColumns, { data: columnsData, isFetching: isColumnsFetching }] = useLazyGetTableColumnsQuery()
  const { urlQueryParams } = useUrlQueryParams()

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(enterpriseOrdersTableHeaderCells, columnsData.data.config)
        : enterpriseOrdersTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "enterprise-orders",
    convertData: convertEnterpriseOrdersNewToTableRows,
    useLazyQuery: useLazyGetEnterpriseOrdersNewQuery,
  })

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetOrders(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    sendRequest(urlQueryParams)
  }, [urlQueryParams])

  useEffect(() => {
    triggerGetTableColumns({ list_name: TABLE_NAME })
  }, [triggerGetTableColumns])

  return (
    <ContentWrapper
      title="Orders history"
      renderHeaderAccessoryView={() => (
        <div className={styles.tableNavButtonsWrapper}>
          <EditColumnsButton tableName={TABLE_NAME} tableHeaderCells={tableHeaderCells} />
          <ButtonNew onClick={handleExport} className={styles.tableNavItem} disabled={data?.meta?.total === 0}>
            Export
          </ButtonNew>
        </div>
      )}
    >
      <TableWrapper
        tableName="enterprise-orders"
        headerCells={tableHeaderCells}
        columnsLoaded={!isColumnsFetching}
        data={convertEnterpriseOrdersNewToTableRows(data?.data)}
        isLoading={isFetching}
        rowsExpanded
      />
      <Pagination meta={data?.meta} />
    </ContentWrapper>
  )
}

export default EnterpriseOrdersPage
